"use client"

import { Box } from "@mui/material"
import { useEffect, useState, useCallback } from "react"
import "./carousel-controller.css"

interface CarouselControllerProps {
  slides: any[]
  interval?: number
  autoplay?: boolean
  showArrows?: boolean
  showDots?: boolean
}

const CarouselController = ({
  slides,
  interval = 5000,
  autoplay = true,
  showArrows = true,
  showDots = true,
}: CarouselControllerProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [hydrated, setHydrated] = useState(false)
  const [mounted, setMounted] = useState(() => new Set([0]))

  const nextSlide = useCallback(() => {
    setActiveIndex(current => (current + 1) % slides.length)
  }, [slides.length])

  const prevSlide = useCallback(() => {
    setActiveIndex(current => (current - 1 + slides.length) % slides.length)
  }, [slides.length])

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(index)
  }, [])

  useEffect(() => {
    setHydrated(true)
  }, [])

  useEffect(() => {
    setMounted(prev => {
      if (prev.has(activeIndex)) return prev
      const next = new Set(prev)
      next.add(activeIndex)
      return next
    })
  }, [activeIndex])

  useEffect(() => {
    if (!hydrated || slides.length < 2) return
    const nextIndex = (activeIndex + 1) % slides.length
    setMounted(prev => {
      if (prev.has(nextIndex)) return prev
      const next = new Set(prev)
      next.add(nextIndex)
      return next
    })
  }, [activeIndex, hydrated, slides.length])

  useEffect(() => {
    if (!autoplay || slides.length < 2) return
    const timer = setInterval(nextSlide, interval)
    return () => clearInterval(timer)
  }, [nextSlide, interval, autoplay, slides.length])

  return (
    <>
      {slides.map((slide, index) => {
        const isActive = index === activeIndex
        if (!mounted.has(index)) {
          return null
        }

        return (
          <Box
            key={slide.id}
            className={`carousel-slide ${isActive ? "active" : ""}`}
          >
            {slide.component}
          </Box>
        )
      })}

      {showArrows && slides.length > 1 && (
        <>
          <button
            className="carousel-arrow carousel-arrow--prev"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            className="carousel-arrow carousel-arrow--next"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </>
      )}

      {showDots && slides.length > 1 && (
        <Box className="carousel-dots">
          {slides.map((_, index) => (
            <Box
              key={index}
              className={`carousel-dot ${index === activeIndex ? "active" : ""}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </Box>
      )}
    </>
  )
}

export default CarouselController
